import { createContext, useContext, useEffect, useState } from "react";

import { type Query, useQueryClient } from "react-query";

import { INBOX_LIMIT } from "@hightouch/lib/inbox/constants";

import {
  type InboxGroupFragment,
  type InboxNotificationFragment,
  type InboxQuery,
  type InboxQueryVariables,
  type InboxUnreadCountQuery,
  type LatestInboxNotificationQuery,
  useInboxQuery,
  useInboxUnreadCountQuery,
  useLatestInboxNotificationQuery,
  useDeleteInboxNotificationsMutation,
  useSetInboxReadMutation,
} from "src/graphql";
import { useGovernedRefetchInterval } from "src/hooks/use-governed-refetch-interval";
import type { InboxFilters } from "src/pages/projects/inbox/inbox-sections";

// The newest notification's time is a cheap way to tell when there is something new.
const CHANGE_POLL_MS = 15_000;
// Read state changed in another tab adds no notification, so the inbox also refetches this often.
const FALLBACK_POLL_MS = 120_000;
const PAGE_SIZE = 50;

export type InboxNotification = InboxNotificationFragment;

export type InboxGroup = InboxGroupFragment;

export type InboxFilterOptions = InboxQuery["inbox"]["filterOptions"];

export type UnreadCount = InboxUnreadCountQuery["inboxUnreadCount"];

const NO_UNREAD: UnreadCount = { count: 0, hasMore: false };

function isInboxQuery(query: Pick<Query, "queryKey">): boolean {
  const [name] = query.queryKey;
  return typeof name === "string" && name.startsWith("Inbox");
}

const selectLatestNotificationAt = (data: LatestInboxNotificationQuery) =>
  data.inboxLatestNotificationAt ?? null;

// Shared by every caller, so a new notification refetches the inbox once however many are mounted.
let seenLatestNotificationAt: string | null | undefined;

/** Refetches the inbox and its count whenever a new notification arrives. */
function useInboxChangeSignal({ isEnabled }: { isEnabled: boolean }) {
  const queryClient = useQueryClient();
  const { refetchInterval } = useGovernedRefetchInterval<string | null>({
    baseMs: CHANGE_POLL_MS,
  });
  const { data: latestNotificationAt } = useLatestInboxNotificationQuery(
    undefined,
    {
      select: selectLatestNotificationAt,
      staleTime: 0,
      enabled: isEnabled,
      refetchInterval,
    },
  );
  useEffect(() => {
    if (latestNotificationAt === undefined) return;
    if (
      seenLatestNotificationAt !== undefined &&
      seenLatestNotificationAt !== latestNotificationAt
    ) {
      void queryClient.invalidateQueries({ predicate: isInboxQuery });
    }
    seenLatestNotificationAt = latestNotificationAt;
  }, [latestNotificationAt, queryClient]);
}

/** How many groups hold an unread notification, for the nav's badge. */
export function useInboxUnreadCount({
  isEnabled,
}: {
  isEnabled: boolean;
}): UnreadCount {
  useInboxChangeSignal({ isEnabled });
  const { refetchInterval } = useGovernedRefetchInterval<UnreadCount>({
    baseMs: FALLBACK_POLL_MS,
    maxMs: FALLBACK_POLL_MS,
  });
  const { data } = useInboxUnreadCountQuery(undefined, {
    enabled: isEnabled,
    select: (result) => result.inboxUnreadCount,
    refetchInterval,
  });
  return data ?? NO_UNREAD;
}

/** "99+" past the count's cap. */
export function formatUnreadCount({ count, hasMore }: UnreadCount): string {
  return hasMore ? `${count}+` : String(count);
}

/** The inbox under these filters with this many pages of update groups loaded. */
export function inboxVariables(
  filters: InboxFilters,
  pages = 1,
): InboxQueryVariables {
  return {
    filters,
    updatesLimit: Math.min(PAGE_SIZE * pages, INBOX_LIMIT),
  };
}

/**
 * The inbox under these filters. Its updates grow a page at a time, asking
 * for every loaded row at once, so a refetch is one request and never leaves
 * a gap where new rows pushed older ones across a page boundary. A new filter
 * starts over at one page.
 */
export function useInbox(filters: InboxFilters) {
  const queryClient = useQueryClient();
  const key = JSON.stringify(filters);
  const [paging, setPaging] = useState({ key, pages: 1 });
  const pages = paging.key === key ? paging.pages : 1;
  const { refetchInterval } = useGovernedRefetchInterval<InboxQuery["inbox"]>({
    baseMs: FALLBACK_POLL_MS,
    maxMs: FALLBACK_POLL_MS,
  });
  const query = useInboxQuery(inboxVariables(filters, pages), {
    select: (data) => data.inbox,
    refetchInterval,
    // Keeps the loaded rows on screen while a longer list loads.
    keepPreviousData: true,
  });
  const loadMore = () => {
    if (query.data?.hasMoreUpdates && !query.isPreviousData) {
      setPaging({ key, pages: pages + 1 });
    }
  };
  // The count too, so a retry after a failed load doesn't leave the badge at zero beside unread rows.
  const refetch = () =>
    queryClient.invalidateQueries({ predicate: isInboxQuery });
  return { ...query, loadMore, refetch };
}

/** The tasks whose review still waits on the viewer, as the open inbox read them with its notifications. */
export const WaitingTaskIdsContext = createContext<
  ReadonlySet<string> | undefined
>(undefined);

/** The tasks whose review still waits on the viewer: undefined while the inbox loads, so nothing reads as no longer waiting before it's known. */
export function useWaitingTaskIds(): ReadonlySet<string> | undefined {
  return useContext(WaitingTaskIdsContext);
}

/** Anything holding notifications: a group, or a row of the list. */
type Notifications = Pick<InboxGroup, "notifications">;

/** The notifications in these, which is what read state and deletes act on. */
function notificationIds(targets: Notifications[]): string[] {
  return targets.flatMap(({ notifications }) =>
    notifications.map((notification) => notification.id),
  );
}

/**
 * Marks every notification in these, or every one the filters keep, read or unread.
 * The app's mutation default refetches the inbox and the badge.
 */
export function useSetInboxRead() {
  const { mutate } = useSetInboxReadMutation();
  return (target: Notifications[] | InboxFilters, isRead: boolean) =>
    mutate(
      Array.isArray(target)
        ? { isRead, ids: notificationIds(target) }
        : { isRead, filters: target },
    );
}

export function useDeleteInboxNotifications() {
  const { mutate } = useDeleteInboxNotificationsMutation();
  return (targets: Notifications[]) =>
    mutate({ ids: notificationIds(targets) });
}

/** The nav's accessible name for the inbox when it holds unread notifications. */
export function unreadLabel(
  label: string,
  unread: UnreadCount,
): string | undefined {
  return unread.count > 0
    ? `${label}, ${formatUnreadCount(unread)} unread`
    : undefined;
}
